<template>
    <CustomDialog
        v-model="visible"
        :defaultTitle="$t('invoices.advanced_filters_title')"
        width="1000px"
        @confirm="applyFilters"
        @cancel="$emit('close')"
    >
        <template #content>
            <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-6 ppv2-px-1">
                <!-- Colonne 1 -->
                <div class="ppv2-space-y-5">
                    <BaseSelect
                        v-model="localFilters.is_refund"
                        :label="$t('invoices.has_refund')"
                        :options="[
                            { label: $t('common.all'), value: null },
                            { label: $t('common.yes'), value: true },
                            { label: $t('common.no'), value: false },
                        ]"
                        fixed-dropdown
                    />

                    <BaseSelect
                        v-model="localFilters.payment_method_id"
                        :label="$t('invoices.payment_method')"
                        :options="availableFilters.paymentMethods"
                        optionLabel="display_name"
                        optionValue="id"
                        :placeholder="$t('common.all')"
                        :clearable="true"
                        fixed-dropdown
                    />
                </div>

                <!-- Colonne 2 -->
                <div class="ppv2-space-y-5">
                    <div class="ppv2-space-y-2">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700">{{
                            $t('invoices.amount')
                        }}</label>
                        <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-2">
                            <BaseInput
                                v-model.number="localFilters.total_greater_than"
                                type="number"
                                :placeholder="$t('common.min')"
                                :customSizeLabel="12"
                            />
                            <BaseInput
                                v-model.number="localFilters.total_lesser_than"
                                type="number"
                                :placeholder="$t('common.max')"
                                :customSizeLabel="12"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Info -->
            <div class="ppv2-mt-6 ppv2-bg-gray-50 ppv2-p-3 ppv2-rounded-lg ppv2-flex ppv2-justify-between ppv2-items-center">
                <div class="ppv2-text-sm ppv2-text-gray-700">
                    <span class="poppinsBold ppv2-text-blue-600">{{ activeFiltersCount }}</span>
                    {{ $t('invoices.active_filters') }}
                </div>
                <button
                    v-if="activeFiltersCount > 0"
                    @click="resetFilters"
                    class="ppv2-text-xs ppv2-text-red-600 hover:ppv2-text-red-800 hover:ppv2-underline"
                >
                    {{ $t('invoices.reset_all') }}
                </button>
            </div>
        </template>

        <template #actions>
            <BaseButton variant="primary" @click="applyFilters" :disabled="saving" customClass="ppv2-px-8">
                <i v-if="saving" class="fas fa-spinner fa-spin ppv2-mr-2"></i>
                {{ saving ? $t('invoices.saving') : $t('invoices.apply_filters') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../Shared/CustomDialog.vue'
import BaseSelect from '../Shared/BaseSelect.vue'
import BaseButton from '../Shared/BaseButton.vue'
import BaseInput from '../Shared/BaseInput.vue'

export default {
    name: 'InvoiceAdvancedFilters',
    components: {
        CustomDialog,
        BaseSelect,
        BaseButton,
        BaseInput,
    },
    props: {
        availableFilters: {
            type: Object,
            required: true,
        },
        currentFilters: {
            type: Object,
            default: () => ({}),
        },
        saveUrl: {
            type: String,
            default: '/back-office/invoices-v3/save-filters?type=invoices_v3_validated',
        },
    },
    data() {
        return {
            visible: true,
            localFilters: {
                validated_order_invoice_id: null,
                is_refund: null,
                payment_method_id: null,
                total_greater_than: null,
                total_lesser_than: null,
            },
            saving: false,
        }
    },
    mounted() {
        if (this.currentFilters && typeof this.currentFilters === 'object' && !Array.isArray(this.currentFilters)) {
            this.localFilters = { ...this.localFilters, ...this.currentFilters }
        }
    },
    computed: {
        activeFiltersCount() {
            let count = 0
            Object.keys(this.localFilters).forEach((key) => {
                const value = this.localFilters[key]
                if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
                    count++
                }
            })
            return count
        },
    },
    methods: {
        resetFilters() {
            this.localFilters = {
                validated_order_invoice_id: null,
                is_refund: null,
                payment_method_id: null,
                total_greater_than: null,
                total_lesser_than: null,
            }
            this.applyFilters()
        },
        async applyFilters() {
            this.saving = true
            try {
                const cleanedFilters = {}
                Object.keys(this.localFilters).forEach((key) => {
                    const value = this.localFilters[key]
                    if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
                        cleanedFilters[key] = value
                    }
                })

                const response = await fetch(this.saveUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        filters: cleanedFilters,
                        type: new URLSearchParams(this.saveUrl.split('?')[1]).get('type') || 'invoices_v3_validated',
                    }),
                })

                const data = await response.json()

                if (response.ok && data.success) {
                    this.$emit('update', data.filters)
                    this.$emit('close')

                    if (this.$toast) {
                        this.$toast.success(this.$t('invoices.filters_applied'))
                    }
                } else {
                    let errorMessage = this.$t('invoices.errors.update_filters')
                    if (response.status === 422 && data.errors) {
                        const errors = Object.values(data.errors).flat()
                        errorMessage = errors.join('\n')
                    } else if (data.message) {
                        errorMessage = data.message
                    }

                    if (this.$toast) {
                        this.$toast.error(this.$t('invoices.errors.validation'))
                    } else {
                        alert(errorMessage)
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
:deep(.custom-input-wrapper) {
    margin-bottom: 0 !important;
}
</style>
